import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonProps,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
} from "@chakra-ui/react";
import { HtmlHTMLAttributes, useCallback, useEffect, useState } from "react";
import { RiTranslate } from "react-icons/ri";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { PreferredLanguageSetting } from "src/hooks/usePreferredLanguage";
import { useTranslationOptions } from "src/hooks/useTranslationOptions";
import { isNonEmptyString } from "src/services/predicates";
import { WEGLOT_APPLY_CLASS, WEGLOT_SKIP_CLASS } from "../constants";
import { WeglotContext, useWeglot, useWeglotInject } from "../hooks";
import { WeglotType } from "../types";

export type PreferredLanguage = { code: string; name: string };

export type WeglotWrapProps = HtmlHTMLAttributes<any> & {
  as?: any;
  skip?: boolean;
  children: React.ReactNode;
};

export type WeglotToggleProps = {
  apply: boolean;
  children: React.ReactNode;
};

const WeglotWrap = ({
  skip = false,
  children,
  as,
  ...props
}: WeglotWrapProps) => {
  const As = as;
  return (
    <As className={skip ? WEGLOT_SKIP_CLASS : WEGLOT_APPLY_CLASS} {...props}>
      {children}
    </As>
  );
};

export const WeglotBlock = ({ children, ...props }: WeglotWrapProps) => (
  <WeglotWrap as="div" {...props}>
    {children}
  </WeglotWrap>
);

export const WeglotText = ({ children, ...props }: WeglotWrapProps) => (
  <WeglotWrap
    as="span"
    style={{ textDecoration: "none", display: "contents" }}
    {...props}
  >
    {children}
  </WeglotWrap>
);

export const WeglotInject: React.FC<
  Omit<WeglotWrapProps, "translateClass">
> = ({ children }) => {
  const [weglot, setWeglot] = useState<WeglotType | undefined>(window.Weglot);
  const handleLoaded = useCallback(() => setWeglot(window.Weglot), []);
  useWeglotInject(handleLoaded);

  return (
    <WeglotContext.Provider value={weglot}>{children}</WeglotContext.Provider>
  );
};

type Props = {
  preferredLanguageSetting: PreferredLanguageSetting;
  colorScheme?: ButtonProps["colorScheme"];
  variant?: ButtonProps["variant"];
};
export const WeglotSelect: React.FC<Props> = ({ ...props }) => {
  const configRD = useTranslationOptions();
  return (
    <RemoteDataView
      remoteData={configRD}
      loading={<Skeleton height="2rem" width="3.25rem" />}
      error={() => null}
    >
      {(config) => (
        <CustomWeglotSelect
          {...props}
          supportedLanguages={config.languages ?? []}
          enabled={config !== null}
        />
      )}
    </RemoteDataView>
  );
};

type CustomProps = {
  enabled: boolean;
  supportedLanguages: readonly string[] | undefined;
} & Props;
const CustomWeglotSelect: React.FC<CustomProps> = ({
  colorScheme = "primary",
  preferredLanguageSetting,
  supportedLanguages,
  enabled,
  variant,
}) => {
  const [langs, setLangs] = useState<PreferredLanguage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const weglot = useWeglot();

  const { preferredLanguage, savePreferredLanguage } = preferredLanguageSetting;

  useEffect(() => {
    // set languages based on supported languages for the organization
    if (!weglot?.initialized) {
      return;
    }

    setLangs(
      weglot.options.languages
        .filter(
          (language) =>
            supportedLanguages?.includes(language.language_to) ?? true
        )
        .map((language) => ({
          code: language.language_to,
          name: weglot.getLanguageName(language.language_to),
        }))
        .concat({
          code: weglot.options.language_from,
          name: weglot.getLanguageName(weglot.options.language_from),
        })
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  }, [supportedLanguages, weglot]);

  const handleLanguageChanged = useCallback(
    (code: string) => {
      savePreferredLanguage(code);
    },
    [savePreferredLanguage]
  );

  useEffect(() => {
    // listen to weglot languageChanged event to keep weglot current language and react's currentLang state in sync
    if (!weglot?.initialized) {
      return;
    }
    weglot.on("languageChanged", handleLanguageChanged);
    return () => weglot.off("languageChanged", handleLanguageChanged);
  }, [handleLanguageChanged, savePreferredLanguage, weglot]);

  const switchLanguage = useCallback(
    (code: string) => {
      weglot?.switchTo(code);
      setIsOpen(false);
    },
    [weglot]
  );

  useEffect(() => {
    if (
      !weglot?.initialized ||
      !isNonEmptyString(preferredLanguage) ||
      supportedLanguages === undefined
    ) {
      return;
    }

    // check if preferred language is supported on this organization
    if (!supportedLanguages.includes(preferredLanguage)) {
      switchLanguage("en");
      return;
    }

    if (preferredLanguage !== weglot.getCurrentLang()) {
      switchLanguage(preferredLanguage);
      return;
    }
  }, [preferredLanguage, supportedLanguages, switchLanguage, weglot]);

  const currentLanguageName =
    langs.find((lang) => lang.code === weglot?.getCurrentLang())?.name ??
    "English";

  return enabled ? (
    <Menu isOpen={isOpen}>
      <MenuButton
        as={Button}
        onClick={() => setIsOpen(!isOpen)}
        colorScheme={colorScheme}
        variant={variant}
        padding="1"
        size="sm"
        _hover={{ background: "none" }}
        _active={{ background: "none" }}
        _focus={{ background: "none" }}
      >
        <Flex alignItems="center" gap={2}>
          <Icon as={RiTranslate} boxSize={4} aria-label="Select language" />
          <Box className={WEGLOT_SKIP_CLASS}>{currentLanguageName}</Box>
          <ChevronDownIcon boxSize={5} />
        </Flex>
      </MenuButton>
      <MenuList
        zIndex={100}
        maxHeight={window.innerHeight - 100}
        overflow="scroll"
      >
        {langs.map((l) => (
          <MenuItem
            className={WEGLOT_SKIP_CLASS}
            key={l.code}
            onClick={() => switchLanguage(l.code)}
            icon={l.code === weglot?.getCurrentLang() ? <CheckIcon /> : <></>}
          >
            {l.name}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  ) : null;
};
